import { ReactNode } from 'react';
import {
  Box,
  CardActionArea,
  IconButton,
  MenuItem,
  Paper,
  TableContainer,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useTranslation } from 'react-i18next';
import { SortCriteria } from '../interfaces/SortCriteria';
import { sharedStyles } from '../utils/Style';

interface ResponsiveRecordsProps {
  desktop: ReactNode;
  mobile: ReactNode;
  children: ReactNode;
}

export const ResponsiveRecords = ({
  desktop,
  mobile,
  children,
}: ResponsiveRecordsProps) => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('lg'));

  if (!isCompact) {
    return (
      <TableContainer component={Paper} sx={sharedStyles.tableContainer}>
        {desktop}
        {children}
      </TableContainer>
    );
  }

  return (
    <Box
      sx={{
        mt: 2,
        minWidth: 0,
        '& .MuiTablePagination-root': { overflow: 'visible' },
        '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', px: 0, gap: 1 },
        '& .MuiTablePagination-spacer': { display: 'none' },
        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
          { fontSize: 12, my: 1 },
        '& .MuiTablePagination-input': { mx: 0 },
        '& .MuiTablePagination-actions': { ml: 'auto' },
      }}
    >
      {mobile}
      {children}
    </Box>
  );
};

interface RecordSortProps<T> {
  criteria: SortCriteria<T>;
  options: { fieldName: keyof T; label: string }[];
  onSort: (fieldName: keyof T) => void;
}

export const RecordSort = <T,>({
  criteria,
  options,
  onSort,
}: RecordSortProps<T>) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      <TextField
        select
        size='small'
        label={t('shared.sort_by')}
        value={String(criteria.fieldName)}
        onChange={(event) => {
          const option = options.find(
            (item) => String(item.fieldName) === event.target.value,
          );
          if (option) onSort(option.fieldName);
        }}
        sx={{ flexGrow: 1, minWidth: 0 }}
      >
        {options.map((option) => (
          <MenuItem
            key={String(option.fieldName)}
            value={String(option.fieldName)}
          >
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      <IconButton
        aria-label={
          t(
            criteria.asc ? 'shared.sort_descending' : 'shared.sort_ascending',
          ) || undefined
        }
        onClick={() => onSort(criteria.fieldName)}
        sx={{ width: 48, height: 48 }}
      >
        {criteria.asc ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
      </IconButton>
    </Box>
  );
};

export const RecordList = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  // Callers pass a mapped array so an empty page can show a helpful message.
  const isEmpty = Array.isArray(children) && children.length === 0;

  return isEmpty ? (
    <Paper sx={{ p: 3, textAlign: 'center' }}>
      <Typography color='text.secondary'>{t('shared.no_results')}</Typography>
    </Paper>
  ) : (
    <Box
      component='ul'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: 0,
        m: 0,
        listStyle: 'none',
      }}
    >
      {children}
    </Box>
  );
};

interface RecordCardProps {
  title: string;
  status?: ReactNode;
  fields: { label: string; value: ReactNode; fullWidth?: boolean }[];
  onOpen?: () => void;
  actions?: ReactNode;
}

export const RecordCard = ({
  title,
  status,
  fields,
  onOpen,
  actions,
}: RecordCardProps) => {
  const { t } = useTranslation();
  const content = (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          pb: 1.5,
          mb: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography
          component='h2'
          sx={{
            fontSize: 16,
            fontWeight: 500,
            minWidth: 0,
            overflowWrap: 'anywhere',
          }}
        >
          {title}
        </Typography>
        {status}
      </Box>
      <Box
        component='dl'
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 2,
          m: 0,
        }}
      >
        {fields.map((field) => (
          <Box
            key={field.label}
            sx={{
              gridColumn: field.fullWidth ? '1 / -1' : undefined,
              minWidth: 0,
            }}
          >
            <Typography
              component='dt'
              sx={{ color: 'text.secondary', fontSize: 12, mb: 0.5 }}
            >
              {field.label}
            </Typography>
            <Box
              component='dd'
              sx={{ m: 0, fontSize: 14, overflowWrap: 'anywhere' }}
            >
              {field.value === '' || field.value == null ? '—' : field.value}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box component='li' sx={{ minWidth: 0 }}>
      <Paper component='article' sx={{ overflow: 'hidden' }}>
        {onOpen ? (
          <CardActionArea
            component='div'
            onClick={onOpen}
            aria-label={`${t('shared.open_details')}: ${title}`}
          >
            {content}
          </CardActionArea>
        ) : (
          content
        )}
        {actions && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              gap: 1,
              borderTop: 1,
              borderColor: 'divider',
              mx: 2,
              py: 1,
              '& .MuiButton-root': { minHeight: 44 },
            }}
          >
            {actions}
          </Box>
        )}
      </Paper>
    </Box>
  );
};
